import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Copy, FileText, Gift, MessageCircle, Search, Share2, Shield } from "lucide-react";
import { toast } from "sonner";

export const AffiliateForm = ({ register, formValues }) => (
  <>
    {/* Referral Reward Settings */}
    <Card className="border-2 border-green-500 bg-green-50" data-testid="affiliate-settings">
      <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-3 py-2">
        <h3 className="font-bold text-white flex items-center gap-2 text-sm">
          <Gift className="h-4 w-4" />
          Referral Reward Settings
        </h3>
      </div>
      <CardContent className="p-3 space-y-3">
        <div className="bg-green-100 border border-green-300 rounded-lg p-3 text-center">
          <p className="text-xs text-green-700 mb-1">Customer earns per referral:</p>
          <div className="flex items-center justify-center gap-2">
            <span className="text-2xl font-bold text-green-700">£</span>
            <Input 
              {...register("affiliate_referral_amount")} 
              type="number"
              className="h-12 w-24 text-2xl font-bold text-center text-green-700 border-green-400" 
            />
          </div>
        </div>

        <div>
          <Label className="text-xs font-semibold">Customer's Unique Referral Code</Label>
          <Input 
            {...register("affiliate_referral_code")} 
            placeholder={`HOMEGO-${formValues.customer_name?.split(' ')[0]?.toUpperCase()?.slice(0,4) || 'JOHN'}123`}
            className="h-9 mt-1 font-mono font-bold tracking-wider text-center border-2 border-dashed border-green-400 bg-white" 
          />
          <p className="text-xs text-gray-500 mt-1">Leave empty to auto-generate based on customer name</p>
        </div>
      </CardContent>
    </Card>

    {/* Headline & Message */}
    <Card className="border-2 border-amber-400">
      <div className="bg-amber-500 px-3 py-2">
        <h3 className="font-bold text-white flex items-center gap-2 text-sm">
          <FileText className="h-4 w-4" />
          Email Content
        </h3>
      </div>
      <CardContent className="p-3 space-y-3">
        <div>
          <Label className="text-xs font-semibold">Headline</Label>
          <Input 
            {...register("affiliate_headline")} 
            placeholder="Earn £100 For Every Friend You Refer!"
            className="h-9 mt-1 font-semibold" 
          />
        </div>

        <div>
          <Label className="text-xs font-semibold">Introduction Message</Label>
          <Textarea 
            {...register("affiliate_intro")} 
            placeholder="Thank you for choosing HomeGo Removals..."
            className="min-h-[80px] mt-1 text-sm" 
          />
        </div>
      </CardContent>
    </Card>

    {/* How It Works Steps */}
    <Card className="border-2 border-blue-400">
      <div className="bg-blue-500 px-3 py-2">
        <h3 className="font-bold text-white flex items-center gap-2 text-sm">
          <Share2 className="h-4 w-4" />
          How It Works (3 Steps)
        </h3>
      </div>
      <CardContent className="p-3 space-y-3">
        <div>
          <Label className="text-xs font-semibold flex items-center gap-2">
            <span className="w-5 h-5 bg-[#D4AF37] rounded-full inline-flex items-center justify-center text-black text-xs font-bold">1</span>
            Step 1 - Share Code
          </Label>
          <Input 
            {...register("affiliate_how_it_works_1")} 
            placeholder="Share your unique referral code..."
            className="h-9 mt-1 text-sm" 
          />
        </div>

        <div>
          <Label className="text-xs font-semibold flex items-center gap-2">
            <span className="w-5 h-5 bg-[#D4AF37] rounded-full inline-flex items-center justify-center text-black text-xs font-bold">2</span>
            Step 2 - Friend Saves
          </Label>
          <Input 
            {...register("affiliate_how_it_works_2")} 
            placeholder="When they book with us..."
            className="h-9 mt-1 text-sm" 
          />
        </div>

        <div>
          <Label className="text-xs font-semibold flex items-center gap-2">
            <span className="w-5 h-5 bg-green-500 rounded-full inline-flex items-center justify-center text-white text-xs font-bold">£</span>
            Step 3 - You Earn!
          </Label>
          <Input 
            {...register("affiliate_how_it_works_3")} 
            placeholder="Once their move is complete..."
            className="h-9 mt-1 text-sm" 
          />
        </div>
      </CardContent>
    </Card>

    {/* Social Share Message */}
    <Card className="border-2 border-purple-400">
      <div className="bg-purple-500 px-3 py-2">
        <h3 className="font-bold text-white flex items-center gap-2 text-sm">
          <MessageCircle className="h-4 w-4" />
          Share Message Template
        </h3>
      </div>
      <CardContent className="p-3 space-y-3">
        <div>
          <Label className="text-xs font-semibold">Message for WhatsApp/Facebook/Twitter</Label>
          <Textarea 
            {...register("affiliate_share_message")} 
            placeholder="I just moved with HomeGo Removals and they were amazing! Use my code for £25 off your move:"
            className="min-h-[60px] mt-1 text-sm" 
          />
          <p className="text-xs text-gray-500 mt-1">This message is pre-filled when customer clicks share buttons</p>
        </div>
      </CardContent>
    </Card>

    {/* Terms & Conditions */}
    <Card className="border-2 border-gray-300">
      <div className="bg-gray-700 px-3 py-2">
        <h3 className="font-bold text-white flex items-center gap-2 text-sm">
          <Shield className="h-4 w-4" />
          Terms & Conditions
        </h3>
      </div>
      <CardContent className="p-3">
        <Textarea 
          {...register("affiliate_terms")} 
          placeholder="Referral must be a new customer. Reward paid within 7 days..."
          className="min-h-[60px] text-sm" 
        />
        <p className="text-xs text-gray-500 mt-1">Displayed at bottom of email in small text</p>
      </CardContent>
    </Card>

    {/* SEO Info Box */}
    <Card className="border-2 border-blue-300 bg-blue-50">
      <CardContent className="p-3">
        <div className="flex items-start gap-2">
          <Search className="h-4 w-4 text-blue-600 mt-0.5" />
          <div>
            <p className="text-xs font-semibold text-blue-800">SEO & AI Optimized</p>
            <p className="text-xs text-blue-600">This email includes:</p>
            <ul className="text-xs text-blue-600 list-disc list-inside mt-1">
              <li>Schema.org structured data for Google</li>
              <li>Open Graph tags for Facebook sharing</li>
              <li>Twitter Card meta tags</li>
              <li>Mobile-responsive design</li>
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>

    {/* WhatsApp Direct Send */}
    <Card className="border-2 border-[#25D366] bg-[#25D366]/10" data-testid="whatsapp-send-card">
      <div className="bg-[#25D366] px-3 py-2">
        <h3 className="font-bold text-white flex items-center gap-2 text-sm">
          <MessageCircle className="h-4 w-4" />
          Send via WhatsApp
        </h3>
      </div>
      <CardContent className="p-3 space-y-3">
        <div>
          <Label className="text-xs font-semibold">Customer's WhatsApp Number</Label>
          <Input 
            {...register("affiliate_whatsapp_number")} 
            placeholder="e.g., 07440414548 or +447440414548"
            className="h-9 mt-1" 
          />
          <p className="text-xs text-gray-500 mt-1">Enter with or without country code</p>
        </div>

        {/* WhatsApp Message Preview */}
        <div>
          <Label className="text-xs font-semibold">WhatsApp Message Preview</Label>
          <div className="bg-[#DCF8C6] rounded-lg p-3 mt-1 text-sm border border-[#25D366]/30">
            <p className="whitespace-pre-wrap text-gray-800">
{`Hi ${formValues.customer_name || 'there'}! 👋

Thank you for choosing *HomeGo Removals*! We hope your move went smoothly. 🏠✨

*🎁 EARN £${formValues.affiliate_referral_amount || '100'} FOR EVERY FRIEND YOU REFER!*

Here's how it works:
1️⃣ Share your unique code with friends & family
2️⃣ They get *£25 OFF* their move
3️⃣ You get *£${formValues.affiliate_referral_amount || '100'} CASH* when they book!

*Your Referral Code:*
📋 *${formValues.affiliate_referral_code || `HOMEGO-${(formValues.customer_name || 'CUST').split(' ')[0].toUpperCase().slice(0,4)}${Math.floor(Math.random() * 1000)}`}*

No limits - refer as many as you like! 🚀

Questions? Call us: 0330 043 4791
Website: www.homego-removals.com

Thank you for being part of the HomeGo family! 💛`}
            </p>
          </div>
        </div>

        {/* Send Button */}
        <Button 
          onClick={() => {
            let phone = formValues.affiliate_whatsapp_number || '';
            // Clean phone number
            phone = phone.replace(/\s/g, '').replace(/[^\d+]/g, '');
            if (phone.startsWith('0')) {
              phone = '44' + phone.slice(1);
            } else if (!phone.startsWith('+') && !phone.startsWith('44')) {
              phone = '44' + phone;
            }
            phone = phone.replace('+', '');
            
            if (!phone || phone.length < 10) {
              toast.error("Please enter a valid WhatsApp number");
              return;
            }

            const refCode = formValues.affiliate_referral_code || 
              `HOMEGO-${(formValues.customer_name || 'CUST').split(' ')[0].toUpperCase().slice(0,4)}${Math.floor(Math.random() * 1000)}`;
            
            const message = `Hi ${formValues.customer_name || 'there'}! 👋

Thank you for choosing *HomeGo Removals*! We hope your move went smoothly. 🏠✨

*🎁 EARN £${formValues.affiliate_referral_amount || '100'} FOR EVERY FRIEND YOU REFER!*

Here's how it works:
1️⃣ Share your unique code with friends & family
2️⃣ They get *£25 OFF* their move
3️⃣ You get *£${formValues.affiliate_referral_amount || '100'} CASH* when they book!

*Your Referral Code:*
📋 *${refCode}*

No limits - refer as many as you like! 🚀

Questions? Call us: 0330 043 4791
Website: www.homego-removals.com

Thank you for being part of the HomeGo family! 💛`;

            const whatsappUrl = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
            window.open(whatsappUrl, '_blank');
            toast.success("Opening WhatsApp...");
          }}
          className="w-full h-12 bg-[#25D366] hover:bg-[#128C7E] text-white text-base font-bold"
        >
          <MessageCircle className="h-5 w-5 mr-2" />
          Send Referral via WhatsApp
        </Button>

        {/* Copy Message Button */}
        <Button 
          variant="outline"
          onClick={() => {
            const refCode = formValues.affiliate_referral_code || 
              `HOMEGO-${(formValues.customer_name || 'CUST').split(' ')[0].toUpperCase().slice(0,4)}${Math.floor(Math.random() * 1000)}`;
            
            const message = `Hi ${formValues.customer_name || 'there'}! 👋

Thank you for choosing HomeGo Removals! We hope your move went smoothly. 🏠✨

🎁 EARN £${formValues.affiliate_referral_amount || '100'} FOR EVERY FRIEND YOU REFER!

Here's how it works:
1️⃣ Share your unique code with friends & family
2️⃣ They get £25 OFF their move
3️⃣ You get £${formValues.affiliate_referral_amount || '100'} CASH when they book!

Your Referral Code:
📋 ${refCode}

No limits - refer as many as you like! 🚀

Questions? Call us: 0330 043 4791
Website: www.homego-removals.com

Thank you for being part of the HomeGo family! 💛`;

            navigator.clipboard.writeText(message);
            toast.success("Message copied to clipboard!");
          }}
          className="w-full h-9 border-[#25D366] text-[#25D366] hover:bg-[#25D366]/10"
        >
          <Copy className="h-4 w-4 mr-2" />
          Copy Message to Clipboard
        </Button>
      </CardContent>
    </Card>
  </>
);
